import { useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useGetList, useNotify, useRefresh, useUpdate } from "react-admin";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";
import { AlertType, Claimant, Client, Policy, ResourceName, StoplossClaim } from "@/types";
import { policyDisplayLabel } from "../../hooks/useStoplossClaimContext";

/**
 * Repair the claim's links (#7586 AC3): client → claimant → policy, cascading,
 * in the claim-item Reassign dialog pattern. Repairs are a correction after a
 * load went wrong, not routine editing, so they live behind an explicit action.
 * Saves the whole record (the PUT handler needs it; #5305).
 */
export const RepairClaimLinksDialog = ({
  open,
  onClose,
  claim,
}: {
  open: boolean;
  onClose: () => void;
  claim: StoplossClaim;
}) => {
  const [update, { isPending }] = useUpdate();
  const refresh = useRefresh();
  const notify = useNotify();
  const notifyError = useRichApiErrorNotify();
  const [clientId, setClientId] = useState<string>(claim.client_id_ref ?? "");
  const [claimantId, setClaimantId] = useState<string>(claim.claimant_id_ref ?? "");
  const [policyId, setPolicyId] = useState<string>(claim.policy_id_ref ?? "");

  const { data: clients = [] } = useGetList<Client>(ResourceName.CLIENTS, {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "client_legal_name", order: "ASC" },
  });
  const { data: claimants = [] } = useGetList<Claimant>(
    ResourceName.CLAIMANTS,
    {
      filter: { client_id_ref: clientId },
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "claimant_name", order: "ASC" },
    },
    { enabled: !!clientId }
  );
  const { data: policies = [] } = useGetList<Policy>(
    ResourceName.POLICIES,
    {
      filter: { client_id_ref: clientId },
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "effective_start_date", order: "DESC" },
    },
    { enabled: !!clientId }
  );

  const reset = () => {
    setClientId(claim.client_id_ref ?? "");
    setClaimantId(claim.claimant_id_ref ?? "");
    setPolicyId(claim.policy_id_ref ?? "");
  };

  const onClaimantChange = (val: Claimant | null) => {
    setClaimantId(val ? String(val.id) : "");
    if (val && !policyId) {
      const active = policies.find((p) => p.status === "active");
      if (active) setPolicyId(String(active.id));
    }
  };

  const chosenPolicy = policies.find((p) => String(p.id) === policyId);
  const autoSelected = chosenPolicy && policyId !== claim.policy_id_ref && chosenPolicy.status === "active";
  const changed =
    clientId !== (claim.client_id_ref ?? "") ||
    claimantId !== (claim.claimant_id_ref ?? "") ||
    policyId !== (claim.policy_id_ref ?? "");

  const save = async () => {
    try {
      await update(
        ResourceName.STOPLOSS_CLAIMS,
        {
          id: claim.id,
          data: { ...claim, client_id_ref: clientId, claimant_id_ref: claimantId, policy_id_ref: policyId },
          previousData: claim,
        },
        { returnPromise: true }
      );
      notify("Claim links updated", { type: AlertType.SUCCESS });
      refresh();
      onClose();
    } catch (e) {
      notifyError(e);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth TransitionProps={{ onEnter: reset }}>
      <DialogTitle>Repair claim links</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Use this when a load linked the claim to the wrong client, claimant or policy. Amounts recompute from the
          claim lines on the next load.
        </Typography>
        <Stack spacing={2}>
          <Autocomplete
            options={clients}
            getOptionLabel={(o) => o.client_legal_name || ""}
            value={clients.find((c) => String(c.id) === clientId) || null}
            onChange={(_, val) => {
              setClientId(val ? String(val.id) : "");
              setClaimantId("");
              setPolicyId("");
            }}
            size="small"
            renderInput={(params) => <TextField {...params} label="Client" />}
          />
          <Autocomplete
            options={claimants}
            getOptionLabel={(o) => o.claimant_name || ""}
            value={claimants.find((c) => String(c.id) === claimantId) || null}
            onChange={(_, val) => onClaimantChange(val)}
            size="small"
            disabled={!clientId}
            renderInput={(params) => <TextField {...params} label="Claimant" />}
          />
          <Box>
            <Autocomplete
              options={policies}
              getOptionLabel={(o) => policyDisplayLabel(o)}
              renderOption={(props, opt) => (
                <li {...props} key={String(opt.id)}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
                    <Typography variant="body2">{policyDisplayLabel(opt)}</Typography>
                    {opt.status === "active" && (
                      <Chip
                        label="Active"
                        size="small"
                        color="success"
                        variant="outlined"
                        sx={{ height: 20, fontSize: "0.65rem" }}
                      />
                    )}
                  </Box>
                </li>
              )}
              value={chosenPolicy || null}
              onChange={(_, val) => setPolicyId(val ? String(val.id) : "")}
              size="small"
              disabled={!clientId}
              renderInput={(params) => <TextField {...params} label="Policy" />}
            />
            {autoSelected && (
              <Typography variant="caption" color="success.main" sx={{ mt: 0.5, display: "block" }}>
                Auto-assigned to the active policy
              </Typography>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => void save()}
          disabled={!clientId || !claimantId || !policyId || !changed || isPending}
          disableElevation
        >
          Save links
        </Button>
      </DialogActions>
    </Dialog>
  );
};
